/**
 * Minimal translation layer with no runtime dependency.
 *
 * Catalogues use the same nested JSON shape as i18next, so switching to
 * i18next later means replacing this file, not the catalogues or the call sites.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import csCatalog from "./catalog/cs.json";
import enCatalog from "./catalog/en.json";
import { getLang, Lang, setLang, subscribeLang } from "./lang";

export type TranslateParams = Record<string, string | number>;

type CatalogNode = string | { [key: string]: CatalogNode };

const catalogs: Record<Lang, Record<string, CatalogNode>> = {
  cs: csCatalog as Record<string, CatalogNode>,
  en: enCatalog as Record<string, CatalogNode>,
};

const lookup = (
  catalog: Record<string, CatalogNode>,
  key: string,
): string | undefined => {
  const value = key.split(".").reduce<CatalogNode | undefined>((node, part) => {
    if (node && typeof node === "object") {
      return node[part];
    }
    return undefined;
  }, catalog);

  return typeof value === "string" ? value : undefined;
};

const interpolate = (text: string, params?: TranslateParams): string => {
  if (!params) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
};

/** Translates a key in the given language, outside React if needed. */
export const translate = (
  key: string,
  params?: TranslateParams,
  lang: Lang = getLang(),
): string => {
  const text = lookup(catalogs[lang], key) ?? lookup(catalogs.cs, key) ?? key;
  return interpolate(text, params);
};

/** Unlike `translate`, never falls back, so callers can supply their own default. */
export const findTranslation = (
  key: string,
  lang: Lang = getLang(),
): string | undefined => lookup(catalogs[lang], key);

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, params?: TranslateParams) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(getLang());

  useEffect(() => {
    document.documentElement.lang = lang;
    return subscribeLang(setLangState);
  }, [lang]);

  const t = useCallback(
    (key: string, params?: TranslateParams) => translate(key, params, lang),
    [lang],
  );

  const value = useMemo<I18nValue>(() => ({ lang, setLang, t }), [lang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = (): I18nValue => {
  const context = useContext(I18nContext);

  if (!context) {
    // Components rendered outside the provider still get Czech text
    // instead of crashing.
    return { lang: getLang(), setLang, t: (key, params) => translate(key, params) };
  }

  return context;
};
